import * as React from 'react'
import {Lang} from 'yougile-lang'
import {saveAsXlsx, Time, Util} from 'yougile-util'
import {
  createDeafultAggregatedReport,
  DataObjectId,
  Department,
  getReportTasks,
  Hub,
  Report,
  ReportColumn,
  TaskData,
  User
} from 'yougile-model'
import {makeTaskDataService} from '@extensions/task-data-helper'
import {Accounts} from '../../../client/accounts'
import {DepartmentsClient} from '../../../client/departments-client'
import {ReportsClient} from '../../../client/reports-client'
import {Sync} from '../../../client/sync'
import {withUpdaters} from '../../../containers/updaters'
import {SyncUpdater} from '../../../containers/updaters/sync-updater'
import {globals} from '../../../extensions/globals-base'
import {Project} from '../../../extensions/project'
import {UserUtils} from '../../../extensions/user-utils'
import {Help} from '../../common/help/help'
import {UserAvatar} from '../../user-avatar'
import {UserName} from '../../user-name'
import {StatsReportsEdit} from './stats-reports-edit'
import {getTasks} from './stats-reports-get-tasks'
import {StatsReportsTable} from './stats-reports-table'

const bem = Util.bemFrom('stats-aggregated-report')

interface Rows {
  user: User[]
  department: Department[]
  project: Hub[]
}

type ReportType = keyof Rows

type ReportValue = User | Department | Hub

interface Props {
  report: Report
  allTasks: TaskData[]
  rows: Rows
  saveReport(report: Report): void
}

interface Cell {
  value: ReportValue
  column: ReportColumn
  ix: ColumnIx
}

const filterRowTasks = (value: ReportValue, all: TaskData[]): TaskData[] => {
  if (value instanceof User) {
    return all.filter(t => t.assigned.includes(value.id))
  } else if (value instanceof Hub) {
    return all.filter(t => t.projectId === value.id)
  } else if (value?.id) {
    return all.filter(t =>
      t.assigned.find(
        id => value.managers.includes(id) || value.members.includes(id)
      )
    )
  } else {
    return undefined
  }
}

const getCellValue = (
  value: ReportValue,
  column: ReportColumn,
  allTasks: TaskData[]
): number => {
  const taskDataService = makeTaskDataService(Sync.getCurrentSandbox())
  const all = getReportTasks(column, allTasks, [], taskDataService)
  return filterRowTasks(value, all)?.length
}

const renderCell = (
  value: ReportValue,
  column: ReportColumn,
  allTasks: TaskData[],
  setCell: (cell: Cell) => void,
  ix: ColumnIx
) => {
  const result = getCellValue(value, column, allTasks) ?? '???'
  const click = () => setCell({column, value, ix})
  return (
    <span className={bem('num')} onClick={click}>
      {result}
    </span>
  )
}

type ColumnIx = number | 'new'

const ixToId = (ix: ColumnIx) => (ix + '') as DataObjectId
const idToIx = (id: DataObjectId): ColumnIx => {
  const s = id as string
  return s === 'new' ? 'new' : +s
}

const reportFromColumn = (
  report: Report,
  col: ReportColumn,
  ix: ColumnIx
): Report => ({
  id: ixToId(ix),
  title: col.title,
  availableTo: [],
  columns: [{...col, fields: report.columns[0]?.fields}]
})

// eslint-disable-next-line max-lines-per-function
const StatsAggregatedReportBase = (props: Props) => {
  const {report, rows, allTasks, saveReport} = props

  const [type, setType] = React.useState<ReportType>('project')
  const groupBy = (v: ReportType) => () => setType(v)
  const [cell, setCell] = React.useState<Cell>(undefined)
  const [sortIx, setSortIx] = React.useState(0)
  const [sortAsc, setSortAsc] = React.useState(true)
  const sortClick = (ix: number) => () => {
    setSortIx(ix)
    setSortAsc(sortIx === ix ? !sortAsc : false)
  }

  const rowValues = rows[type] as ReportValue[]
  let sortedRows = [...rowValues]
  if (sortIx === 0) {
    if (!sortAsc) {
      sortedRows = sortedRows.reverse()
    }
  } else {
    const ix: Map<DataObjectId, number> = new Map()
    sortedRows.forEach(r => {
      ix.set(r.id, getCellValue(r, report.columns[sortIx - 1], allTasks))
    })
    sortedRows.sort(
      (a, b) => ((ix.get(a.id) ?? 0) - (ix.get(b.id) ?? 0)) * (sortAsc ? 1 : -1)
    )
  }

  // report here is from a column
  const [edited, setEdited] = React.useState<Report>(undefined)
  const editColumn =
    (col: ReportColumn, ix: number) => (e: React.MouseEvent) => {
      e.stopPropagation()
      setEdited(reportFromColumn(report, col, ix))
    }
  const addColumn = () => {
    const col: ReportColumn = {
      filter: {},
      title: '',
      sort: [{key: 'createdAt', asc: false}]
    }
    setEdited(reportFromColumn(report, col, 'new'))
  }

  const downloadXlsx = () => {
    const now = Time.now()
    const data: Array<Array<string | number>> = []
    const header = [
      Lang.msg.report.groupTitle[type],
      ...report.columns.map(col => col.title)
    ]
    data.push(header)
    rowValues.map(r => {
      data.push([
        r instanceof User ? Accounts.getName(r.id) : r.title,
        ...report.columns.map(
          col => (getCellValue(r, col, allTasks) ?? '') + ''
        )
      ])
    })
    saveAsXlsx(report.title + ' ' + Time.formatDateTime(now) + '.xlsx', data)
  }
  const close = () => setEdited(undefined)
  const save = (changedColumReport: Report) => {
    const col: ReportColumn = {
      ...changedColumReport.columns[0],
      title: changedColumReport.title
    }
    const colIx = idToIx(changedColumReport.id)
    if (colIx === 'new') {
      saveReport({...report, columns: [...report.columns, col]})
    } else {
      const newReport = {
        ...report,
        columns: [
          ...report.columns.slice(0, colIx),
          col,
          ...report.columns.slice(colIx + 1)
        ]
      }
      newReport.columns[0].fields = col.fields
      saveReport(newReport)
    }
  }
  const del = async () => {
    const colIx = idToIx(edited.id)
    if (colIx !== 'new') {
      saveReport({
        ...report,
        columns: [
          ...report.columns.slice(0, colIx),
          ...report.columns.slice(colIx + 1)
        ]
      })
    }
    return true
  }

  if (cell) {
    const back = () => setCell(undefined)
    const tasks = filterRowTasks(cell.value, allTasks)
    const columnReport = reportFromColumn(report, cell.column, cell.ix)
    return (
      <div className={bem()}>
        <div className={bem('back')} onClick={back}>
          {Lang.msg.back}
        </div>
        <StatsReportsTable
          update={updater => save(updater(columnReport))}
          report={columnReport}
          tasks={tasks}
        />
      </div>
    )
  } else {
    return (
      <div className={bem()}>
        {!!edited && (
          <StatsReportsEdit
            initialReport={edited}
            close={close}
            deleteReport={del}
            saveReport={save}
            canEdit={true}
            isAggregatedColumn={true}
          />
        )}
        <div className={bem('type')}>
          <div className={bem('nav')}>
            <div
              className={bem('nav-item', {active: type === 'project'})}
              onClick={groupBy('project')}
            >
              {Lang.msg.report.groupProject}
            </div>
            <div
              className={bem('nav-item', {active: type === 'department'})}
              onClick={groupBy('department')}
            >
              {Lang.msg.report.groupDepartment}
            </div>
            <div
              className={bem('nav-item', {active: type === 'user'})}
              onClick={groupBy('user')}
            >
              {Lang.msg.report.groupUser}
            </div>
          </div>
          <Help name="mainReport" className={bem('help')} />
          <div className={bem('space')} />
          <div className={bem('xlsx')} onClick={downloadXlsx}>
            {Lang.msg.statsDownloadXlsx}
          </div>
        </div>
        <div className={bem('table-cnt')}>
          <table>
            <thead>
              <tr>
                <th
                  className={bem('head-cell', {
                    first: true,
                    sorted: sortIx === 0,
                    asc: sortAsc
                  })}
                  onClick={sortClick(0)}
                >
                  {Lang.msg.report.groupTitle[type]}
                </th>
                {report.columns.map((col, ix) => (
                  <th
                    className={bem('head-cell', {
                      sorted: ix + 1 === sortIx,
                      asc: sortAsc
                    })}
                    key={ix}
                    onClick={sortClick(ix + 1)}
                  >
                    <span className={bem('col')} onClick={editColumn(col, ix)}>
                      {col.title}
                    </span>
                  </th>
                ))}
                <th>
                  <div className={bem('add-column-btn')} onClick={addColumn}>
                    {Lang.msg.report.addColumn}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => (
                <tr key={r.id}>
                  <td>
                    {r instanceof User ? (
                      <>
                        <UserAvatar size={18} user={r} /> <UserName user={r} />
                      </>
                    ) : (
                      r.title
                    )}
                  </td>
                  {report.columns.map((col, ix) => (
                    <td key={ix}>
                      {renderCell(r, col, allTasks, setCell, ix)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }
}

export const StatsAggregatedReport = withUpdaters(
  SyncUpdater({companyType: 'current', filter: '*', debounceTime: 200})
)(() => {
  const box = Sync.getCurrentSandbox()
  const userId = globals.currentUserId

  const rc = new ReportsClient(box)
  const report =
    rc.getAggregatedReport() ?? createDeafultAggregatedReport(Lang.msg, userId)
  const allUsers = UserUtils.sortByName(box.getAllUsers(false))
  const rows: Rows = {
    user: allUsers,
    department: DepartmentsClient.listDepartments(box).sort((a, b) =>
      a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1
    ),
    project: Project.listProjects(box.companyId, 'not-archive', true).sort(
      (a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1)
    )
  }
  const allTasks = getTasks(box, {title: '', filter: {}})
  const save = (changedReport: Report) => {
    rc.saveReport(changedReport)
  }

  return (
    <StatsAggregatedReportBase
      report={report}
      rows={rows}
      allTasks={allTasks}
      saveReport={save}
    />
  )
})
